import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import OfferCapture from "@/components/OfferCapture";
import { activeOffer, OFFER_IS_PROVISIONAL } from "@/data/offer";
import { trackEvent, getStoredUTM } from "@/lib/tracking";

// THE MAGNET PAGE.
//
// Deliberately not a page in the site's usual shape. Everything on /books and
// /shop is designed to let a reader browse; this page is designed to let them
// decide. One offer, one field, one button, and no navigation competing with it
// — a second call to action here is a leak, not a courtesy.
//
// Everything visible comes from frontend/src/data/offer.js. Switching from a
// wallpaper pack to a graphic-novel chapter is one line there; this file does
// not know or care which offer it is rendering.

export default function Offer() {
  const offer = activeOffer();

  useEffect(() => {
    // The denominator. Without an arrival event, an opt-in rate has no bottom
    // half and "the magnet is working" is an opinion.
    const utm = getStoredUTM();
    trackEvent("view_content", {
      title_id: offer.sku,
      slug: offer.id,
      offer_id: offer.id,
      vertical: offer.vertical,
      surface: "offer",
      arrived_via: utm.utm_content || null,
    });

    if (process.env.NODE_ENV !== "production" && OFFER_IS_PROVISIONAL) {
      // eslint-disable-next-line no-console
      console.warn(
        `[offer] ACTIVE_OFFER_ID="${offer.id}" is PROVISIONAL. Start-lock #1 ` +
          `requires the buyer interviews to name the primary SKU. Set ` +
          `OFFER_IS_PROVISIONAL = false in frontend/src/data/offer.js once they have.`
      );
    }
  }, [offer]);

  const isLandscape = offer.art?.orientation === "landscape";

  return (
    <PageShell title={offer.title}>
      <section className="max-w-6xl mx-auto px-5 md:px-10 lg:px-14 py-10 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Art. On mobile it leads — the picture is the argument. */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 order-1"
          >
            <div
              className={`card-shrine rounded-sm overflow-hidden bg-parchment-deep ${
                isLandscape ? "aspect-[3/2]" : "aspect-[4/5]"
              }`}
            >
              {offer.art?.hero ? (
                <img
                  src={offer.art.hero}
                  alt={offer.art.alt || ""}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-parchment via-parchment-deep to-dawn-rose/40">
                  <span className="font-display text-5xl text-dharma-gold-deep/30">ॐ</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* The offer. */}
          <div className="lg:col-span-7 order-2">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="eyebrow mb-4">{offer.eyebrow}</div>
              <h1 className="font-display text-3xl md:text-5xl lg:text-[3.4rem] tracking-tightish text-ink leading-[1.06] text-balance">
                {offer.title}
              </h1>
              <p className="font-serif text-lg md:text-2xl text-ink-soft mt-5 leading-relaxed max-w-2xl text-pretty">
                {offer.subtitle}
              </p>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 space-y-2.5"
            >
              {offer.promise.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-1 rounded-full bg-dharma-gold shrink-0" />
                  <span className="font-serif text-base md:text-lg text-ink-soft leading-relaxed">
                    {line}
                  </span>
                </li>
              ))}
            </motion.ul>

            <div className="rule-gold my-9" />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <OfferCapture offer={offer} sourceKey="offer" />
            </motion.div>

            {offer.onward && (
              <p className="mt-9 font-body text-xs tracking-widest2 uppercase">
                <Link
                  to={offer.onward.to}
                  onClick={() =>
                    trackEvent("offer_onward_click", {
                      title_id: offer.sku,
                      slug: offer.id,
                      offer_id: offer.id,
                      to: offer.onward.to,
                    })
                  }
                  className="inline-flex items-center gap-1.5 text-dharma-gold-deep hover:text-saffron-fire transition-colors"
                >
                  {offer.onward.label}
                  <ArrowRight size={12} />
                </Link>
              </p>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
